// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import axios from "axios"

// Types
import type { SuccessFetch } from "types"
import type { DataCountType } from "pages/Inventori/Transaction/types"
import { PicturesType } from "../../../AddNewItem/types"
import type { FormType } from "../vars"

// Utils
import { useApi } from "utils"
import { getValuesByDifferent } from "pages/Inventori/Transaction/utils"

export function useDataAdjustmentDetail(adjustment_id: string, isDetail?: boolean): {
  data: DataCountType | null
  formData: FormType | null
  isLoading: boolean
  refetch: () => void 
} {
  // Hooks
  const api = useApi()
  const [data, setData] = useState<DataCountType | null>(null)
  const [formData, setFormData] = useState<FormType | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)

  const fetchData = () => {
    setLoading(true)
    api.get("/invadjustment/detailinvadjustment", {
      params: { adjustment_id }
    }).then((res: SuccessFetch<{
      payload: {
        invcount: {
          count_id: number
          employee_id: number
          employee_name: string
          location_id: number
          location_name: string
          memo: string
          transaction_date: string
          trx_code: string
        }[]
        adjdetail: {
          product_id: number,
          mr_unit_id: number,
          product_barcode: string,
          different_adjustment: string,
          different_unit: number,
          amount_adjustment: number,
          position: number,
          coa_inventory_id: number,
          coa_coex_id: number
          product_name: string,
          mr_unit_name: string,
          coa_inv_code: string,
          coa_inv_name: string,
          coa_ex_code: string,
          coa_ex_name: string,
          avg_price_use: number,
          unit_convention_name: string,
          quantity_unit_sell_convention: number,
          soh: number,
          adjustment_detail_id: number
        }[]
      }
    }>) => {
      const payload = res.data.payload
      const adjDetail = payload.adjdetail
      const invCount = payload.invcount[0]

      if (!invCount?.count_id || isDetail) {
        setFormData({
          employee_id: invCount.employee_id.toString(),
          location_id: invCount.location_id.toString(),
          memo: invCount.memo,
          transaction_date: invCount.transaction_date,
          trx_code: invCount.trx_code
        })
        setData({
          invcount: invCount,
          countdetail: adjDetail.map(item => {
            const differentAdjustment = Number(item.different_adjustment)
            const adjustmentQty = Math.abs(differentAdjustment)
            const isDebet = item.position === 1

            return {
              avg_price_use: Number(item.avg_price_use),
              coa_inventory_code: item.coa_inv_code,
              coa_inventory_id: item.coa_inventory_id,
              coa_inventory_name: item.coa_inv_name,
              coa_coex_code: item.coa_ex_code,
              coa_coex_id: item.coa_coex_id,
              coa_coex_name: item.coa_ex_name,
              count_detail_id: 0,
              different: differentAdjustment,
              mr_unit_id: item.mr_unit_id,
              product_id: item.product_id,
              quantity_unit_sell_convention: item.quantity_unit_sell_convention,
              real_count: 0,
              soh_count: Number(item.soh),
              product_barcode: item.product_barcode,
              product_name: item.product_name,
              mr_unit_name: item.mr_unit_name,
              pictures: [],
              position: isDebet ? "debet" : "credit",
              stock_in: isDebet ? adjustmentQty : 0,
              stock_out: isDebet ? 0 : adjustmentQty,
              amount_adjustment: item.amount_adjustment,
              different_adjustment: item.different_adjustment,
              different_unit: item.different_unit,
              unit_convention_name: item.unit_convention_name,
              soh: item.soh,
              adjustment_detail_id: item.adjustment_detail_id
            }
          })
        })
        setLoading(false)
        return
      }

      // get with count 
      axios.all([
        api.get("/invcount/detailinvcount", {
          params: { count_id: invCount.count_id }
        }),
        api.get("/linkedaccount/bycode", {
          params: { linked_account_code: "LA10" }
        })
      ]).then(axios.spread((res, laRes) => {
        const data: SuccessFetch<{
          payload: {
            countdetail: {
              avg_price_use: string
              coa_asset_code: string
              coa_asset_id: number
              coa_asset_name: string
              coa_expense_code: string
              coa_expense_id: number
              coa_expense_name: string
              count_detail_id: number
              different: string
              mr_unit_id: number
              product_id: number
              quantity_unit_sell_convention: string
              real_count: number
              soh_count: number
              product_barcode: string
              product_name: string
              mr_unit_name: string
              pictures?: PicturesType[]
              amount_adjustment: number,
              different_adjustment: string,
              different_unit: number,
              unit_convention_name: string
              adjustment_detail_id: number
            }[]
            invcount: {
              count_id: number
              employee_id: number
              employee_name: string
              location_id: number
              location_name: string
              memo: string
              transaction_date: string
              trx_code: string
            }[]
          }
        }> = res

        const laData: SuccessFetch<{
          payload: {
            coa_id: number
            coa_code: string
            coa_name: string
          }[]
        }> = laRes

        const payload = data.data.payload
        const laPayload = laData.data.payload[0]

        setFormData({
          employee_id: invCount.employee_id.toString(),
          location_id: invCount.location_id.toString(),
          memo: invCount.memo,
          transaction_date: invCount.transaction_date,
          trx_code: invCount.trx_code
        })

        setData({
          invcount: payload.invcount[0],
          countdetail: payload.countdetail.map(item => {
            const quantity_unit_sell_convention = parseInt(item.quantity_unit_sell_convention)
            const different = Number(item.different)
            const differentValue = getValuesByDifferent(different)
            const isCredit = differentValue.position === "credit"

            return {
              ...item,
              different,
              quantity_unit_sell_convention: quantity_unit_sell_convention > 0 ? quantity_unit_sell_convention : 1,
              avg_price_use: parseInt(item.avg_price_use),
              coa_inventory_code: item.coa_asset_code,
              coa_inventory_id: item.coa_asset_id,
              coa_inventory_name: item.coa_asset_name,
              coa_coex_code: isCredit ? laPayload.coa_code : item.coa_expense_code,
              coa_coex_id: isCredit ? laPayload.coa_id : item.coa_expense_id,
              coa_coex_name: isCredit ? laPayload.coa_name : item.coa_expense_name,
              position: differentValue.position,
              stock_in: differentValue.stock_in,
              stock_out: differentValue.stock_out,
              amount_adjustment: item.amount_adjustment,
              different_adjustment: item.different_adjustment,
              different_unit: item.different_unit,
              unit_convention_name: item.unit_convention_name,
              adjustment_detail_id: item.adjustment_detail_id
            }
          })
        })
      })).catch(() => {
        setData(null)
        setFormData(null)
      }).finally(() => {
        setLoading(false)
      })
    }).catch(() => {
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchData()

    return () => {
      setData(null)
      setFormData(null)
      setLoading(false)
    }

    // eslint-disable-next-line
  }, [adjustment_id])

  return {
    data,
    formData,
    isLoading,
    refetch: fetchData 
  }
}
